<template>
  <div class="card mb-4">
    <h5 class="card-header">
      <i class="fas fa-shield-alt mr-1"></i>Carrier Trust &amp; Risk
    </h5>
    <div class="card-body">
      <div class="trust-grid">
        <div class="trust-cell">
          <div class="cell-label">Trust Score</div>
          <div class="cell-value" :class="scoreColorClass">{{ trustScore }} / 100</div>
          <div class="cell-sub">{{ scoreLabel }}</div>
        </div>

        <div class="trust-cell">
          <div class="cell-label">Risk Grade</div>
          <div class="cell-value grade-badge" :class="gradeColorClass">{{ carrier.riskGrade || 'A' }}</div>
          <div class="cell-sub">{{ gradeLabel }}</div>
        </div>

        <div class="trust-cell">
          <div class="cell-label">Data Confidence</div>
          <div class="cell-value confidence">{{ carrier.dataConfidence || 'New Data' }}</div>
          <div class="cell-sub">Based on {{ carrier.completedOrdersCount || 0 }} completed orders</div>
        </div>

        <div class="trust-cell">
          <div class="cell-label">Dispatch Status</div>
          <div class="cell-value" :class="carrier.canDispatchEffective ? 'text-success' : 'text-danger'">
            <i :class="carrier.canDispatchEffective ? 'fas fa-check-circle' : 'fas fa-ban'" class="mr-1"></i>
            {{ carrier.canDispatchEffective ? 'ALLOWED' : 'BLOCKED' }}
          </div>
          <div class="cell-sub">{{ carrier.canDispatchEffective ? 'Eligible for dispatch' : 'Not eligible for dispatch' }}</div>
        </div>

        <div class="trust-cell">
          <div class="cell-label">Block Reason</div>
          <div v-if="carrier.blockReason" class="cell-value text-danger">
            <i class="fas fa-exclamation-triangle mr-1"></i>{{ carrier.blockReason }}
          </div>
          <div v-else-if="unpaidBrokerFee > 0" class="cell-value text-warning">
            <i class="fas fa-info-circle mr-1"></i>Notice
          </div>
          <div v-else class="cell-value text-muted">—</div>
          <div v-if="unpaidBrokerFee > 0" class="cell-sub text-warning">
            <strong>Unpaid broker fee:</strong> {{ String(unpaidBrokerFee) | formatMoney }}
          </div>
          <div v-else class="cell-sub">&nbsp;</div>
        </div>

        <div class="trust-cell">
          <div class="cell-label">Last Incident</div>
          <div v-if="carrier.lastIncidentAt" class="cell-value">
            <i class="far fa-calendar mr-1"></i>{{ lastIncidentRelative }}
          </div>
          <div v-else class="cell-value text-muted">-</div>
          <div v-if="carrier.lastIncidentAt" class="cell-sub">
            {{ carrier.lastIncidentAt | formatDate }} * {{ formatIncidentType(carrier.lastIncidentType) }}
          </div>
          <div v-else class="cell-sub">No incidents recorded</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CarrierTrustPanel',
  props: {
    carrier: {
      type: Object,
      required: true,
    },
  },
  computed: {
    trustScore() {
      const s = this.carrier.trustScore;
      if (s === null || s === undefined) return 100;
      return s;
    },
    unpaidBrokerFee() {
      return parseFloat(this.carrier.unpaidBrokerFeeAmount) || 0;
    },
    scoreColorClass() {
      const g = this.carrier.riskGrade;
      if (g === 'A') return 'text-success';
      if (g === 'B') return 'text-success';
      if (g === 'C') return 'text-warning';
      if (g === 'D') return 'text-danger';
      return 'text-danger';
    },
    scoreLabel() {
      const g = this.carrier.riskGrade;
      if (g === 'A') return 'Excellent';
      if (g === 'B') return 'Good';
      if (g === 'C') return 'Watch';
      if (g === 'D') return 'Risky';
      return 'Block';
    },
    gradeColorClass() {
      const g = this.carrier.riskGrade;
      if (g === 'A') return 'grade-a';
      if (g === 'B') return 'grade-b';
      if (g === 'C') return 'grade-c';
      if (g === 'D') return 'grade-d';
      return 'grade-block';
    },
    gradeLabel() {
      const g = this.carrier.riskGrade;
      if (g === 'A') return 'Excellent';
      if (g === 'B') return 'Good';
      if (g === 'C') return 'Watch';
      if (g === 'D') return 'Risky';
      return 'Manager approval';
    },
    lastIncidentRelative() {
      if (!this.carrier.lastIncidentAt) return '';
      const incidentDate = new Date(this.carrier.lastIncidentAt);
      const now = new Date();
      const diffMs = now - incidentDate;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 30) return `${diffDays} days ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    },
  },
  methods: {
    formatIncidentType(type) {
      if (!type) return '';
      const map = {
        cancellation: 'Cancellation',
        cancellation_24h: 'Cancellation (24h)',
        no_show: 'No-show',
        ghosting: 'Ghosting',
        late_pickup: 'Late pickup',
        late_delivery: 'Late delivery',
        communication: 'Failure to update',
        false_status: 'False status',
        damage: 'Damage',
        damage_severe: 'Damage (severe)',
      };
      return map[type] || type;
    },
  },
};
</script>

<style lang="scss" scoped>
.trust-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

@media (max-width: 1200px) {
  .trust-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 700px) {
  .trust-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.trust-cell {
  border: 1px solid #e6e8eb;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
  background: #fafbfc;
  color: #1f2937;
  min-height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.dark-style .trust-cell {
  background: #2a2e35;
  border-color: #3a3f47;
  color: #e5e7eb;
}

.cell-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.dark-style .cell-label {
  color: #9ca3af;
}

.cell-value {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 6px 0 4px;
  color: inherit;
}

.cell-sub {
  font-size: 0.75rem;
  color: #6b7280;
}

.dark-style .cell-sub {
  color: #9ca3af;
}

.dark-style .text-success { color: #34d399 !important; }
.dark-style .text-warning { color: #fbbf24 !important; }
.dark-style .text-danger { color: #f87171 !important; }
.dark-style .text-muted { color: #6b7280 !important; }

.grade-badge {
  display: inline-block;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  line-height: 44px;
  margin: 0 auto;
  color: #fff;
  font-size: 1.4rem;
}

.grade-a { background-color: #16a34a; }
.grade-b { background-color: #ca8a04; }
.grade-c { background-color: #ea580c; }
.grade-d { background-color: #dc2626; }
.grade-block { background-color: #7f1d1d; }

.confidence {
  font-size: 1rem;
}
</style>
